import React, { useRef, useState } from "react";
import SideBar from "../SideBar";
import axiosClient from "../../axios";
import loaderimg from "../../assets/images/loader.gif";
import CSVDownload from "../CSVDownload";
import "../../assets/css/AdminCommon.css";

const SEARCH_BY = {
	id: 1,
	email: 2,
	phone: 3,
};

const readFileAsText = (file) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = (e) => resolve(e.target.result);
		reader.onerror = reject;
		reader.readAsText(file);
	});

const toIsoDate = (dateStr, endOfDay = false) => {
	if (!dateStr) return null;
	return endOfDay
		? `${dateStr}T23:59:59.999Z`
		: `${dateStr}T00:00:00.000Z`;
};

const formatCoins = (value) => {
	const n = Number(value);
	if (!Number.isFinite(n)) return "0";
	return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const formatUsd = (coins) => {
	const n = Number(coins);
	if (!Number.isFinite(n)) return "$0.00";
	return `$${(n / 1000).toFixed(2)}`;
};

const formatCount = (value) => {
	const n = Number(value);
	if (!Number.isFinite(n)) return "0";
	return Math.round(n).toLocaleString();
};

const isZeroDay = (day) =>
	(Number(day?.earners) || 0) === 0 &&
	(Number(day?.user_earning) || 0) === 0 &&
	(Number(day?.our_earning) || 0) === 0;

const CsvUserDayEarnings = () => {
	const fileInputRef = useRef(null);
	const [bulkData, setBulkData] = useState([]);
	const [csvFileName, setCsvFileName] = useState("");
	const [searchBy, setSearchBy] = useState(String(SEARCH_BY.id));
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [loader, setLoader] = useState(false);
	const [activeTab, setActiveTab] = useState("daywise");
	const [expandedDay, setExpandedDay] = useState(null);
	const [result, setResult] = useState(null);

	const handleBulkCsv = async (event) => {
		const file = event.target.files?.[0];
		if (!file) return;

		try {
			const text = await readFileAsText(file);
			const expectedHeaders = ["emailId", "phoneNumber", "userId"];
			const fileHeaders = text
				.split("\n")[0]
				.split(",")
				.map((header) => header.trim());

			if (!expectedHeaders.every((header, index) => header === fileHeaders[index])) {
				alert("Invalid CSV format. Headers must be: emailId, phoneNumber, userId");
				return;
			}

			const rows = text.split("\n").slice(1);
			const formattedData = rows
				.map((row) => row.split(","))
				.filter((cols) => cols.length >= 2)
				.map(([emailId = "", phoneNumber = "", userId = ""]) => ({
					emailId: emailId.trim(),
					phoneNumber: phoneNumber.trim(),
					userId: userId.trim(),
				}))
				.filter((row) => row.emailId || row.phoneNumber || row.userId);

			if (formattedData.length === 0) {
				alert("No valid rows found in CSV");
				return;
			}

			setBulkData(formattedData);
			setCsvFileName(file.name);
			setResult(null);
		} catch (err) {
			console.error(err);
			alert("Failed to read CSV file");
		}
	};

	const handleFetchReport = async () => {
		if (bulkData.length === 0) {
			alert("Please upload a CSV file first");
			return;
		}
		if (!startDate || !endDate) {
			alert("Please select start and end date");
			return;
		}
		if (startDate > endDate) {
			alert("Start date cannot be after end date");
			return;
		}

		const payload = {
			bulkData,
			searchBy: Number(searchBy),
			s_date: toIsoDate(startDate),
			e_date: toIsoDate(endDate, true),
		};

		setLoader(true);
		setResult(null);
		setExpandedDay(null);

		try {
			const res = await axiosClient.post("/admin/csv-users-earnings-daywise", payload);
			if (res?.data?.status === false) {
				alert(res?.data?.message || "Failed to fetch day-wise earnings");
				return;
			}
			setResult(res?.data?.response ?? null);
		} catch (err) {
			const msg =
				err?.response?.data?.message ||
				err?.response?.data?.messae ||
				"Failed to fetch day-wise earnings";
			alert(msg);
		} finally {
			setLoader(false);
		}
	};

	const handleDownloadUnmatched = () => {
		const unmatched = result?.unmatched_users ?? [];
		if (!unmatched.length) {
			alert("No unmatched users to download");
			return;
		}
		const rows = unmatched.map((row) => ({
			"Email Id": row.emailId ?? "",
			"Phone Number": row.phoneNumber ?? "",
			userId: row.userId ?? "",
		}));
		CSVDownload(rows, "unmatched_users.csv");
	};

	const dayWise = result?.day_wise ?? [];
	const summary = result?.summary ?? {};
	const matchedUsers = result?.matched_users_list ?? [];

	// Partner-wise month totals come directly from the backend (already unique
	// per partner across the range and sorted by user_earning desc). We must NOT
	// derive these by summing day_wise.by_partner — a user earning on multiple
	// days would be counted multiple times and inflate the earner count.
	const partnerTotals = summary.month_unique_earners_by_partner ?? [];

	const today = new Date().toISOString().split("T")[0];

	return (
		<>
			<SideBar handleSideBar={true} activeTab="csvUserDayEarnings" />
			<div className="admin-screen-v4">
				<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
					<div className="admin-header-v4">
						<div>
							<h2>CSV User Day Earnings</h2>
							<p style={{ margin: "8px 0 0 0", color: "var(--text-muted)", fontSize: "14px" }}>
								Upload a user list (email, phone, or user ID) and pick a date range to see daily
								earnings for those users — with partner-wise breakdown per day.
							</p>
						</div>
					</div>
				</div>

				<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
					<h3 className="section-header-v4">Upload &amp; Filters</h3>

					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
							gap: "var(--spacing-lg)",
							marginBottom: "var(--spacing-lg)",
						}}
					>
						<div className="form-group-v4">
							<label className="form-label-v4">Start Date</label>
							<input
								type="date"
								className="form-input-v4"
								value={startDate}
								max={endDate || today}
								onChange={(e) => setStartDate(e.target.value)}
							/>
						</div>
						<div className="form-group-v4">
							<label className="form-label-v4">End Date</label>
							<input
								type="date"
								className="form-input-v4"
								value={endDate}
								min={startDate || undefined}
								max={today}
								onChange={(e) => setEndDate(e.target.value)}
							/>
						</div>
					</div>

					<div style={{ marginBottom: "var(--spacing-lg)" }}>
						<label className="form-label-v4" style={{ display: "block", marginBottom: 8 }}>
							Match users by
						</label>
						<div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
							{[
								{ value: String(SEARCH_BY.id), label: "User ID" },
								{ value: String(SEARCH_BY.email), label: "Email" },
								{ value: String(SEARCH_BY.phone), label: "Phone" },
							].map((opt) => (
								<label
									key={opt.value}
									style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}
								>
									<input
										type="radio"
										name="searchBy"
										value={opt.value}
										checked={searchBy === opt.value}
										onChange={(e) => setSearchBy(e.target.value)}
									/>
									<span style={{ fontSize: 14 }}>{opt.label}</span>
								</label>
							))}
						</div>
					</div>

					<div className="csvData-upload-section-v4">
						<div className="csvData-upload-text">
							CSV headers: <strong>emailId, phoneNumber, userId</strong> — at least one field per row
							should match based on your selected match type.
						</div>
						<label htmlFor="csvUserDayEarningsUpload" className="upload-label-v4-csvData">
							<span className="upload-text-v4">
								{csvFileName ? csvFileName : "Click to upload CSV file"}
							</span>
						</label>
						<input
							ref={fileInputRef}
							type="file"
							id="csvUserDayEarningsUpload"
							accept=".csv"
							onChange={handleBulkCsv}
							className="file-input-v4"
						/>
					</div>

					{bulkData.length > 0 && (
						<p style={{ marginTop: 12, fontSize: 13, color: "var(--text-muted)" }}>
							{bulkData.length.toLocaleString()} rows loaded from CSV
						</p>
					)}

					<div style={{ marginTop: "var(--spacing-lg)" }}>
						<button
							type="button"
							className="btn-primary-v4"
							onClick={handleFetchReport}
							disabled={loader}
						>
							{loader ? "Loading…" : "Get Day-wise Earnings"}
						</button>
					</div>
				</div>

				{loader && (
					<div className="admin-form-container-v4" style={{ textAlign: "center", padding: 40 }}>
						<img src={loaderimg} alt="Loading" style={{ width: 48, height: 48 }} />
					</div>
				)}

				{!loader && result && (
					<>
						<div className="kpi-grid-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
							<div className="kpi-card-v4">
								<p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>CSV Rows</p>
								<p style={{ margin: "8px 0 0", fontSize: 24, fontWeight: 700 }}>
									{bulkData.length.toLocaleString()}
								</p>
							</div>
							<div className="kpi-card-v4">
								<p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>Matched Rows</p>
								<p style={{ margin: "8px 0 0", fontSize: 24, fontWeight: 700 }}>
									{formatCount(result.matched_count)}
								</p>
							</div>
							<div className="kpi-card-v4">
								<p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>Unmatched Rows</p>
								<p style={{ margin: "8px 0 0", fontSize: 24, fontWeight: 700, color: "#D97706" }}>
									{formatCount(result.unmatched_count ?? result.unmatched_users?.length)}
								</p>
							</div>
							<div className="kpi-card-v4">
								<p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>Unique Earners (Month)</p>
								<p style={{ margin: "8px 0 0", fontSize: 24, fontWeight: 700 }}>
									{formatCount(summary.month_unique_earners)}
								</p>
								<p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--text-muted)" }}>
									Unique earners across the selected range
								</p>
							</div>
							<div className="kpi-card-v4">
								<p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>Total User Earning</p>
								<p style={{ margin: "8px 0 0", fontSize: 24, fontWeight: 700 }}>
									{formatCoins(summary.total_user_earning)} coins
								</p>
								<p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--text-muted)" }}>
									{formatUsd(summary.total_user_earning)}
								</p>
							</div>
							<div className="kpi-card-v4">
								<p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>Total Our Earning</p>
								<p style={{ margin: "8px 0 0", fontSize: 24, fontWeight: 700 }}>
									{formatCoins(summary.total_our_earning)} coins
								</p>
								<p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--text-muted)" }}>
									{formatUsd(summary.total_our_earning)}
								</p>
							</div>
						</div>

						{(result.unmatched_users?.length ?? 0) > 0 && (
							<div style={{ marginBottom: "var(--spacing-lg)" }}>
								<button type="button" className="btn-secondary-v4" onClick={handleDownloadUnmatched}>
									Download Unmatched CSV
								</button>
							</div>
						)}

						<div className="admin-form-container-v4">
							<div
								style={{
									display: "flex",
									gap: 8,
									marginBottom: 20,
									flexWrap: "wrap",
									background: "#F3F4F6",
									padding: 4,
									borderRadius: 8,
									width: "fit-content",
								}}
							>
								{[
									{ id: "daywise", label: "Day-wise" },
									{ id: "partner", label: "Partner-wise (month)" },
									{ id: "users", label: "Matched Users" },
								].map((tab) => (
									<button
										key={tab.id}
										type="button"
										onClick={() => setActiveTab(tab.id)}
										style={{
											padding: "8px 16px",
											border: "none",
											borderRadius: 6,
											fontSize: 13,
											fontWeight: 600,
											cursor: "pointer",
											background: activeTab === tab.id ? "#fff" : "transparent",
											color: activeTab === tab.id ? "#3B82F6" : "#6B7280",
											boxShadow: activeTab === tab.id ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
										}}
									>
										{tab.label}
									</button>
								))}
							</div>

							{activeTab === "daywise" && (
								<div className="table-container-v4">
									{dayWise.length === 0 ? (
										<p style={{ color: "var(--text-muted)" }}>No day-wise earnings in this range.</p>
									) : (
										<table className="data-table-v4">
											<thead>
												<tr>
													<th>Date</th>
													<th>Earners</th>
													<th>User Earning</th>
													<th>Our Earning</th>
													<th>Partners</th>
												</tr>
											</thead>
											<tbody>
												{dayWise.map((day, idx) => (
													<React.Fragment key={day.date ?? idx}>
														<tr style={isZeroDay(day) ? { color: "#9CA3AF" } : undefined}>
															<td style={{ fontWeight: 600 }}>{day.date}</td>
															<td>{formatCount(day.earners)}</td>
															<td>
																{formatCoins(day.user_earning)}
																<span style={{ fontSize: 12, color: "#6B7280", marginLeft: 6 }}>
																	({formatUsd(day.user_earning)})
																</span>
															</td>
															<td>
																{formatCoins(day.our_earning)}
																<span style={{ fontSize: 12, color: "#6B7280", marginLeft: 6 }}>
																	({formatUsd(day.our_earning)})
																</span>
															</td>
															<td>
																<button
																	type="button"
																	className="btn-secondary-v4"
																	style={{ padding: "4px 10px", fontSize: 12 }}
																	onClick={() =>
																		setExpandedDay(expandedDay === idx ? null : idx)
																	}
																>
																	{expandedDay === idx ? "Hide" : "Show"} (
																	{day.by_partner?.length ?? 0})
																</button>
															</td>
														</tr>
														{expandedDay === idx && (day.by_partner?.length ?? 0) > 0 && (
															<tr>
																<td colSpan={5} style={{ background: "#F9FAFB", padding: 16 }}>
																	<table className="data-table-v4" style={{ margin: 0 }}>
																		<thead>
																			<tr>
																				<th>Partner</th>
																				<th>Earners</th>
																				<th>User Earning</th>
																				<th>Our Earning</th>
																			</tr>
																		</thead>
																		<tbody>
																			{day.by_partner.map((p, pi) => (
																				<tr key={p.partner_id ?? pi}>
																					<td>{p.partner_name ?? p.partner_id}</td>
																					<td>{formatCount(p.earners)}</td>
																					<td>{formatCoins(p.user_earning)}</td>
																					<td>{formatCoins(p.our_earning)}</td>
																				</tr>
																			))}
																		</tbody>
																	</table>
																</td>
															</tr>
														)}
													</React.Fragment>
												))}
											</tbody>
										</table>
									)}
								</div>
							)}

							{activeTab === "partner" && (
								<div className="table-container-v4">
									{partnerTotals.length === 0 ? (
										<p style={{ color: "var(--text-muted)" }}>No partner earnings in this range.</p>
									) : (
										<table className="data-table-v4">
											<thead>
												<tr>
													<th>Partner</th>
													<th>Unique Earners (month)</th>
													<th>User Earning</th>
													<th>Our Earning</th>
												</tr>
											</thead>
											<tbody>
												{partnerTotals.map((p, i) => (
													<tr key={p.partner_id ?? i}>
														<td>{p.partner_name ?? p.partner_id ?? "—"}</td>
														<td>{formatCount(p.earners)}</td>
														<td>
															{formatCoins(p.user_earning)}
															<span style={{ fontSize: 12, color: "#6B7280", marginLeft: 6 }}>
																({formatUsd(p.user_earning)})
															</span>
														</td>
														<td>
															{formatCoins(p.our_earning)}
															<span style={{ fontSize: 12, color: "#6B7280", marginLeft: 6 }}>
																({formatUsd(p.our_earning)})
															</span>
														</td>
													</tr>
												))}
											</tbody>
										</table>
									)}
								</div>
							)}

							{activeTab === "users" && (
								<div className="table-container-v4">
									{matchedUsers.length === 0 ? (
										<p style={{ color: "var(--text-muted)" }}>No matched users found.</p>
									) : (
										<table className="data-table-v4">
											<thead>
												<tr>
													<th>S.No.</th>
													<th>Name</th>
													<th>Email</th>
													<th>User ID</th>
													<th>Phone</th>
												</tr>
											</thead>
											<tbody>
												{matchedUsers.map((u, i) => (
													<tr key={u._id ?? i}>
														<td>{i + 1}</td>
														<td
															style={{ color: "#2563EB", cursor: "pointer" }}
															onClick={() => u._id && window.open(`/users/transaction/${u._id}`)}
														>
															{u.name ?? "—"}
														</td>
														<td>{u.emailId ?? "—"}</td>
														<td>{u._id ?? u.userId ?? "—"}</td>
														<td>{u.phoneNumber ?? "—"}</td>
													</tr>
												))}
											</tbody>
										</table>
									)}
								</div>
							)}
						</div>
					</>
				)}
			</div>
		</>
	);
};

export default CsvUserDayEarnings;
